import { apiFetch } from '@/lib/api';
import type { EngineAuthContext } from '@/lib/auth';

// Pricing + credit-model client (seitiate-plus #104). Every number the customer
// sees is rendered from these responses — nothing is hardcoded in the UI.

export interface PricingTier {
  tier_key: string;
  display_name: string;
  monthly_price_usd: number;
  included_credits_usd: number;
  /** Configured credit count, or null if the tier still uses the legacy dollar grant. */
  credit_grant_mode?: "compatibility" | "price_default" | "fixed_total";
  included_credits: number | null;
  /** Always populated — the count to show on the card ("{monthly_credits} credits / month"). */
  monthly_credits: number;
  surfaces: string[];
  limits: Record<string, unknown>;
  tagline: string | null;
  features: string[];
  sort_order: number;
  is_default: boolean;
}

export interface CreditTransaction {
  id: string;
  transaction_type: string;
  amount_usd: number;
  amount_credits: number;
  description: string | null;
  created_by: string | null;
  created_at: string;
  expires_at: string | null;
}

export interface CreditBalance {
  balance_usd: number;
  /** Server-derived credit count; USD remains the held value basis. */
  balance_credits: number;
  credit_exchange_rate: number;
  /** This subscription period's allowance; forfeited at expires_at. */
  expiring_credits: number;
  /** Promo + purchased; never expire. */
  permanent_credits: number;
  expires_at: string | null;
  recent_transactions: CreditTransaction[];
}

/** Public — no auth required. */
export async function getPricingTiers(): Promise<PricingTier[]> {
  return apiFetch<PricingTier[]>('/api/v1/pricing/tiers');
}

// A statement captures these headers for its private cache lifetime. Override both
// transport defaults so a delayed/retried request cannot adopt another session.
function statementHeaders(context?: EngineAuthContext): RequestInit {
  return context ? { headers: {
    Authorization: context.token ? `Bearer ${context.token}` : '',
    'X-Active-Tenant-ID': context.activeTenantId,
  } } : {};
}

/** Signed-in only; recent_transactions is a ten-row window, not full history. */
export async function getCreditBalance(verticalId: string, context?: EngineAuthContext): Promise<CreditBalance> {
  const params = new URLSearchParams({ vertical_id: verticalId });
  return apiFetch<CreditBalance>(`/api/v1/pricing/credits/balance?${params.toString()}`, statementHeaders(context));
}

/** Signed-in only; the endpoint supports at most 200 rows and no cursor. */
export async function getCreditHistory(verticalId: string, limit = 50, context?: EngineAuthContext): Promise<CreditTransaction[]> {
  const params = new URLSearchParams({ vertical_id: verticalId, limit: String(limit) });
  return apiFetch<CreditTransaction[]>(`/api/v1/pricing/credits/history?${params.toString()}`, statementHeaders(context));
}

/**
 * The PLATFORM base family (Seitiate+ managed surface) uses BARE tier keys —
 * free / amateur / professional — not a "platform_" prefix, because the legacy
 * env-var Paddle path resolves the platform row by bare key. Every other vertical
 * uses a "<vertical>_" prefix. ``tiersForVertical`` keys off this distinction so
 * /subscribe/platform renders the base tiers and /subscribe/<vertical> renders
 * only that vertical's prefixed tiers (no cross-family bleed).
 */
export const PLATFORM_TIER_KEYS = ['free', 'amateur', 'professional'] as const;

/** Tiers belonging to one vertical, sorted. Platform = bare keys; others = prefix. */
export function tiersForVertical(tiers: PricingTier[], verticalId: string): PricingTier[] {
  const platformKeys = new Set<string>(PLATFORM_TIER_KEYS);
  const matches =
    verticalId === 'platform'
      ? (tier: PricingTier) => platformKeys.has(tier.tier_key)
      : (tier: PricingTier) => tier.tier_key.startsWith(`${verticalId}_`);
  return tiers.filter(matches).sort((a, b) => a.sort_order - b.sort_order);
}

// ── LLM price catalog ────────────────────────────────────────────────────
// Backed by price_catalog, the same table the managed-inference charge is
// computed from — so what a customer is quoted and what they are billed come
// from one row. Before this, per-token rates were hardcoded in
// `src/data/llmPricing.ts`: on 2026-09-07 that table disagreed with the engine
// on 10 of 29 models, under-quoted four Perplexity models, and still advertised
// five model ids that return 404.

export interface LlmPriceCatalogEntry {
  provider: string;
  model: string;
  resource_type: string;
  quality_tier: string | null;
  display_name: string | null;
  input_cost_per_1m: number | null;
  output_cost_per_1m: number | null;
}

export async function fetchLlmPriceCatalog(options?: { signal?: AbortSignal }): Promise<LlmPriceCatalogEntry[]> {
  return apiFetch<LlmPriceCatalogEntry[]>('/api/v1/pricing/catalog?resource_type=llm', {
    signal: options?.signal,
  });
}

/** Index by the engine's canonical model id, which is what `LlmModel.modelId`
 *  carries — the only key the UI data and the billing catalog share. */
export function indexCatalogByModelId(
  entries: LlmPriceCatalogEntry[],
): Map<string, LlmPriceCatalogEntry> {
  return new Map(entries.map((e) => [e.model, e]));
}
